import * as React from "react";
import { Box, Paper, Typography, Stack, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import { Aircraft } from "../../../../types";
import { getAircraftImageUrl } from "../utils/getAircraftImageUrl";

interface Props {
  selectedAircraft: Aircraft | null;
  imageError: boolean;
  onImageError: () => void;
  aircraftMap: Record<string, string>;
}

export const AircraftPreview: React.FC<Props> = ({
  selectedAircraft,
  imageError,
  onImageError,
  aircraftMap,
}) => {
  const [localImageError, setLocalImageError] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    setLocalImageError(false);
  }, [selectedAircraft]);

  if (!selectedAircraft) {
    return (
      <Paper
        sx={{
          width: "100%",
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <Typography color="textSecondary">Select an aircraft to preview</Typography>
      </Paper>
    );
  }

  const hasError = imageError || localImageError;
  const imageUrl = getAircraftImageUrl(selectedAircraft.id);

  return (
    <Paper
      sx={{
        width: "100%",
        p: 3,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Stack spacing={2} sx={{ height: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">
            {selectedAircraft.make} {selectedAircraft.model}
          </Typography>
          <Button
            variant="contained"
            size="medium"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/labeling-tool/${selectedAircraft.id}`, { 
              state: { 
                from: '/super-admin', 
                selectedAircraftId: selectedAircraft.id,
                activeTab: 10  // Aircraft tab index
              }
            })}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
        </Box>
        
        <Box
          sx={{
            flex: "1 1 60%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "grey.100",
            borderRadius: 1,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {hasError ? (
            <Typography color="textSecondary">No image available</Typography>
          ) : (
            <img
              src={imageUrl}
              alt={`${selectedAircraft.make} ${selectedAircraft.model}`}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
              onError={() => {
                setLocalImageError(true);
                onImageError();
              }}
            />
          )}
        </Box>

        <Box sx={{ flex: "0 0 auto", overflow: "auto" }}>
          <Stack spacing={1}>
            <Typography variant="body2">
              <strong>ID:</strong> {selectedAircraft.id}
            </Typography>
            <Typography variant="body2">
              <strong>Length:</strong> {selectedAircraft.length?.toFixed(1) || "-"} ft
            </Typography>
            <Typography variant="body2">
              <strong>Wingspan:</strong> {selectedAircraft.wingspan?.toFixed(1) || "-"} ft
            </Typography>
            <Typography variant="body2">
              <strong>Tail Height:</strong> {selectedAircraft.tail_height?.toFixed(1) || "-"} ft
            </Typography>
            {selectedAircraft.linked_to && (
              <Typography variant="body2">
                <strong>Linked To:</strong> {aircraftMap[selectedAircraft.linked_to] || selectedAircraft.linked_to}
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};